import { useState } from 'react'

import { ALL_TEDDIES } from '@hazel/shared'

import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { Input } from '../components/Input.js'
import { Modal } from '../components/Modal.js'
import { Tally } from '../components/Tally.js'

type Scale = 'compact' | 'default' | 'tv'

const SCALES: readonly Scale[] = ['compact', 'default', 'tv']

const SWATCHES = [
  ['surface', 'bg-surface'],
  ['surface-raised', 'bg-surface-raised'],
  ['surface-sunken', 'bg-surface-sunken'],
  ['primary', 'bg-primary'],
  ['coral', 'bg-coral'],
  ['teal', 'bg-teal'],
  ['amber', 'bg-amber'],
  ['lime', 'bg-lime'],
] as const

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-(length:--text-heading) text-ink-muted">{title}</h2>
      {children}
    </section>
  )
}

/**
 * Every token and component, at every scale.
 *
 * The scale switcher is the point of this page. A component that looks right on
 * a laptop can be unreadable on the TV or cramped on a phone, and without
 * somewhere to see all three at once those regressions are only found by
 * carrying a laptop to the sofa.
 */
export function StyleGuide() {
  const [scale, setScale] = useState<Scale>('default')
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-8 p-6">
      <header className="flex flex-col gap-3">
        <h1 className="text-(length:--text-hero)">Hazel’s Games style guide</h1>
        <div className="flex flex-wrap gap-2">
          {SCALES.map((option) => (
            <Button
              key={option}
              variant={scale === option ? 'primary' : 'secondary'}
              onClick={() => setScale(option)}
              aria-pressed={scale === option}
            >
              {option}
            </Button>
          ))}
        </div>
        <p className="text-ink-muted">
          Preview below renders at the <strong>{scale}</strong> scale. TV scale is what the host
          display uses, read from a sofa rather than at arm&rsquo;s length.
        </p>
      </header>

      <div
        data-scale={scale === 'default' ? undefined : scale}
        className="border-border flex flex-col gap-8 rounded-(--radius-card) border-2 border-dashed p-6"
      >
        <Section title="Type scale">
          <p className="text-(length:--text-hero) font-display">Hero — Hoppity&rsquo;s Hangman</p>
          <p className="text-(length:--text-title) font-display">Title — Monkey&rsquo;s Maths</p>
          <p className="text-(length:--text-heading) font-display">Heading — Sky&rsquo;s Memory</p>
          <p className="text-(length:--text-body)">
            Body — the quick brown fox jumps over the lazy dog.
          </p>
          <p className="text-(length:--text-small) text-ink-muted">Small — supporting detail.</p>
        </Section>

        <Section title="Colour">
          <div className="flex flex-wrap gap-3">
            {SWATCHES.map(([name, bg]) => (
              <div key={name} className="flex flex-col items-center gap-1">
                <span className={`border-border size-16 rounded-(--radius-control) border ${bg}`} />
                <span className="text-(length:--text-small) text-ink-muted">{name}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Buttons">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Play</Button>
            <Button variant="secondary">Change teddy</Button>
            <Button variant="quiet">Not now</Button>
            <Button disabled>Waiting</Button>
          </div>
        </Section>

        <Section title="Inputs">
          <div className="flex flex-col gap-4">
            <Input label="Username" placeholder="hazel" hint="This is what you type to log in." />
            <Input label="Password" type="password" error="That password didn't match." />
          </div>
        </Section>

        <Section title="Tallies">
          <div className="flex flex-wrap gap-3">
            <Tally label="words solved" value={12} tone="teal" />
            <Tally label="games played" value={24} tone="amber" />
            <Tally label="pairs found" value={10} tone="lime" />
            <Tally label="rounds today" value={3} tone="coral" />
          </div>
        </Section>

        <Section title="Teddies">
          <div className="flex flex-wrap items-end gap-3">
            {ALL_TEDDIES.slice(0, 6).map((teddy) => (
              <div key={teddy.key} className="flex flex-col items-center gap-1">
                <Avatar teddy={teddy.key} name={teddy.name} />
                <span className="text-(length:--text-small) text-ink-muted">{teddy.name}</span>
              </div>
            ))}
            <div className="flex flex-col items-center gap-1">
              <Avatar teddy={null} name="Hazel" />
              <span className="text-(length:--text-small) text-ink-muted">no photo</span>
            </div>
          </div>
        </Section>

        <Section title="Card and modal">
          <Card>
            <h3 className="text-(length:--text-heading)">Hoppity&rsquo;s Hangman</h3>
            <p className="text-ink-muted">Guess the word one letter at a time.</p>
            <div className="mt-4">
              <Button onClick={() => setModalOpen(true)}>Open a modal</Button>
            </div>
          </Card>
          <Modal open={modalOpen} title="Play again?" onClose={() => setModalOpen(false)}>
            <p className="mb-4">The word was RABBIT. Fancy another go?</p>
            <div className="flex gap-2">
              <Button onClick={() => setModalOpen(false)}>Yes please</Button>
              <Button variant="quiet" onClick={() => setModalOpen(false)}>
                Later
              </Button>
            </div>
          </Modal>
        </Section>
      </div>
    </div>
  )
}
